import { useTranslation } from 'react-i18next'
import { RotateCcw } from 'lucide-react'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import {
  PERMISSION_LEVELS,
  PERMISSION_MODULES,
  type PermissionLevel,
  type PermissionMatrix as Matrix,
  type PermissionModule,
  type WebRole,
} from '@/types'

interface PermissionMatrixProps {
  value: Matrix
  onChange: (next: Matrix) => void
  onReset: () => void
  disabled?: boolean
  /** Target role — Settings row is hidden for Operator (always no_access). */
  targetRole?: WebRole | '' | null
}

const thClass =
  'h-12 border-b border-[#edf0f5] bg-[#f8f9fb] px-3 py-2 text-[10px] font-medium uppercase leading-[14px] tracking-[0.02em] text-[#6c7285]'

const tdClass = 'h-12 border-b border-[#edf0f5] px-3 py-2 align-middle'
const radioBaseClass =
  'inline-flex size-5 items-center justify-center rounded-full border bg-white transition-colors disabled:cursor-not-allowed'

function PermissionRadio({
  checked,
  disabled,
  label,
  onChange,
}: {
  checked: boolean
  disabled?: boolean
  label: string
  onChange: () => void
}) {
  return (
    <button
      type='button'
      role='radio'
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      className={cn(
        radioBaseClass,
        checked
          ? 'border-[4px] border-[#22b07d]'
          : 'border-[#d5dbe6] bg-white hover:border-[#b9c2d3]',
        disabled && 'opacity-50'
      )}
    />
  )
}

// 2.11.3 - Module permission control. Rows = modules, columns = access levels
// (radio per row). Lives inline on the Create/Edit user form (2.11.2).
export function PermissionMatrix({
  value,
  onChange,
  onReset,
  disabled,
  targetRole,
}: PermissionMatrixProps) {
  const { t } = useTranslation('settings')

  const visibleModules =
    targetRole === 'Operator'
      ? PERMISSION_MODULES.filter(m => m !== 'settings')
      : PERMISSION_MODULES

  function setLevel(module: PermissionModule, level: PermissionLevel) {
    if (module === 'settings' && targetRole === 'Operator') return
    onChange({ ...value, [module]: level })
  }

  return (
    <section className='rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_4px_rgba(38,45,66,0.05),0_8px_16px_-12px_rgba(38,45,66,0.1)]'>
      <header className='flex flex-wrap items-center justify-between gap-3 px-4 pb-2 pt-4 sm:px-6'>
        <div className='flex min-w-0 items-center gap-3'>
          <span className='inline-flex size-6 shrink-0 items-center justify-center rounded-lg bg-[#ff5a1f] text-xs font-medium text-white'>
            2
          </span>
          <h2 className='min-w-0 font-heading text-sm font-semibold leading-[1.2] text-[#171a20]'>
            {t('users.permissions.title')}
          </h2>
        </div>
        <button
          type='button'
          onClick={onReset}
          disabled={disabled}
          className='inline-flex shrink-0 items-center gap-2 rounded-full px-3 py-2 text-xs font-medium leading-none text-[#ff5a1f] transition-colors hover:bg-[#fff1ea] disabled:pointer-events-none disabled:opacity-50'
        >
          <RotateCcw className='size-4' />
          {t('users.permissions.resetToDefault')}
        </button>
      </header>

      <div className='overflow-x-auto px-4 pb-4 pt-2 sm:px-6 sm:pb-6'>
        <table className='w-full min-w-[560px] table-fixed caption-bottom overflow-hidden rounded-xl border border-[#edf0f5] text-sm'>
          <TableHeader>
            <TableRow className='border-0'>
              <TableHead className={cn(thClass, 'w-[28%]')}>
                {t('users.permissions.moduleColumn')}
              </TableHead>
              {PERMISSION_LEVELS.map(level => (
                <TableHead
                  key={level}
                  className={cn(thClass, 'w-[18%] px-1 text-center')}
                >
                  <span className='block whitespace-normal leading-4'>
                    {t(`users.levels.${level}`)}
                  </span>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleModules.map(module => (
              <TableRow key={module}>
                <TableCell className={tdClass}>
                  <span className='text-sm font-medium leading-[1.4] text-[#3d434e]'>
                    {t(`users.modules.${module}`)}
                  </span>
                </TableCell>
                {PERMISSION_LEVELS.map(level => (
                  <TableCell
                    key={level}
                    className={cn(tdClass, 'px-1 text-center')}
                  >
                    <PermissionRadio
                      checked={value[module] === level}
                      disabled={disabled}
                      label={`${t(`users.modules.${module}`)} - ${t(`users.levels.${level}`)}`}
                      onChange={() => setLevel(module, level)}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </table>
      </div>
    </section>
  )
}

export default PermissionMatrix
